import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import React from 'react';

import { useTickets } from 'contexts/TicketsContext';
import { TicketsRefinementKeysE } from 'declarations/enums/tickets';
import type { Column } from 'declarations/types/common';

import TicketsReadStatusSelect from '../TicketsActionDropDown/TicketsActionDropDown';
import useTicketsRefinement from '../TicketsRefinement/hooks/useTicketsRefinement';

import {
  CHECKBOX_COLUMN_WIDTH_PX,
  COLUMN_LABEL_TO_SORT_PARAM,
  COLUMN_MIN_WIDTH_PX,
  NON_SORTABLE_COLUMN_LABELS,
  VIEWED_INDICATOR_COLUMN_WIDTH_PX,
  getDataColumnWidth,
} from './TicketsTableConfiguration';

type TicketsSortingProps = {
  selectedTicketsIds: string[];
  columns: Column[];
  dataColumnWidthsPx: number[] | null;
  totalWeight: number;
  numSelected: number;
  rowCount: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const TicketsSorting: FC<TicketsSortingProps> = ({
  columns,
  dataColumnWidthsPx,
  totalWeight,
  numSelected,
  rowCount,
  onSelectAllClick,
}) => {
  const { getSearchParam, selectedTicketsIds, setSelectedTicketsIds } = useTickets();
  const { handleUpdateSortingClick, isAscSortingActive } = useTicketsRefinement();

  const currentSortParam = getSearchParam(TicketsRefinementKeysE.Parameter);
  const sortDirection: 'asc' | 'desc' = isAscSortingActive ? 'asc' : 'desc';

  const visibleColumns = columns.filter(col => col.visible);

  return (
    <TableHead>
      <TableRow>
        <TableCell
          padding="checkbox"
          style={{ width: CHECKBOX_COLUMN_WIDTH_PX, minWidth: CHECKBOX_COLUMN_WIDTH_PX }}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: theme => theme.palette.background.default,
            boxShadow: theme => `0 1px 0 0 ${theme.palette.divider}`,
            overflow: 'visible',
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
            <Tooltip title="Number of tickets selected" arrow>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: 13, cursor: 'default' }}
              >
                {numSelected}
              </Typography>
            </Tooltip>
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'Select all tickets' }}
            />
          </Box>
        </TableCell>

        <TableCell
          style={{
            width: VIEWED_INDICATOR_COLUMN_WIDTH_PX,
            minWidth: VIEWED_INDICATOR_COLUMN_WIDTH_PX,
          }}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: theme => theme.palette.background.default,
            boxShadow: theme => `0 1px 0 0 ${theme.palette.divider}`,
            padding: '0 4px',
            verticalAlign: 'middle',
          }}
        >
          <TicketsReadStatusSelect
            selectedTickets={selectedTicketsIds}
            setSelectedTickets={setSelectedTicketsIds}
          />
        </TableCell>

        {visibleColumns.map((col, idx) => {
          const sortParam = COLUMN_LABEL_TO_SORT_PARAM[col.label];
          const isSortable = !!sortParam && !NON_SORTABLE_COLUMN_LABELS.has(col.label);
          const isActive = !!sortParam && currentSortParam === sortParam;
          const width = dataColumnWidthsPx?.[idx] ?? getDataColumnWidth(col.label, totalWeight);
          const minWidth = COLUMN_MIN_WIDTH_PX[col.label] ?? 56;

          return (
            <TableCell
              key={col.id}
              align={
                col.label === 'P' ||
                col.label === 'Age' ||
                col.label === 'Claim Age' ||
                col.label === 'Closed RO Age'
                  ? 'center'
                  : 'left'
              }
              sortDirection={isActive ? sortDirection : false}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 100,
                bgcolor: 'background.default',
                boxShadow: theme => `inset 0 -1px 0 ${theme.palette.divider}`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                width,
                minWidth,
              }}
            >
              {isSortable ? (
                <TableSortLabel
                  active={isActive}
                  direction={isActive ? sortDirection : 'asc'}
                  onClick={handleUpdateSortingClick(TicketsRefinementKeysE.Parameter, sortParam)}
                >
                  <Typography variant="body2" noWrap>
                    {col.label}
                  </Typography>
                </TableSortLabel>
              ) : (
                <Typography variant="body2" noWrap>
                  {col.label}
                </Typography>
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

export default TicketsSorting;
